import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';
import { showToast } from '../toast';

interface TvSignal {
  receivedAt: string;
  signal:     string;
  ticker:     string;
  exchange?:  string;
  price?:     string;
  chart_tf?:  string;
  tv_time?:   string;
}

const SIGNAL_COLORS: Record<string, string> = {
  breakout:   'var(--green)',
  breakdown:  'var(--red)',
  buy:        'var(--green)',
  sell:       'var(--red)',
  long:       'var(--green)',
  short:      'var(--red)',
  alert:      'var(--warn)',
};

function signalColor(signal: string): string {
  const s = (signal || '').toLowerCase();
  for (const [key, col] of Object.entries(SIGNAL_COLORS)) {
    if (s.includes(key)) return col;
  }
  return 'var(--accent)';
}

export class SignalsTab implements Tab {
  readonly id = 'signals';
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private lastCount = 0;

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    // Sub-nav
    document.getElementById('signals-sub-chart')  ?.addEventListener('click', () => (window as any).showTab?.('charts'));
    document.getElementById('signals-sub-scanner') ?.addEventListener('click', () => (window as any).showTab?.('scanner'));
    document.getElementById('signals-sub-signals') ?.addEventListener('click', () => (window as any).showTab?.('signals'));

    this.el('signals-refresh-btn').addEventListener('click', () => this.load());
    (this.el('signals-ticker-filter') as HTMLInputElement)
      .addEventListener('input', () => this.load());
  }

  render(): void {
    this.load();
    if (!this.refreshTimer) {
      this.refreshTimer = setInterval(() => this.load(), 30000);
    }
  }

  destroy(): void {
    if (this.refreshTimer) { clearInterval(this.refreshTimer); this.refreshTimer = null; }
  }

  private async load(): Promise<void> {
    const ticker = (this.el('signals-ticker-filter') as HTMLInputElement).value.trim().toUpperCase();
    const path   = '/api/signals' + (ticker ? `?ticker=${encodeURIComponent(ticker)}` : '');

    try {
      const data = await workerFetch<{ count: number; signals: TvSignal[] }>(path);
      const signals = data.signals ?? [];

      // Badge new signals
      if (signals.length > this.lastCount && this.lastCount > 0) {
        showToast(`${signals.length - this.lastCount} new TradingView signal(s)`, 'success');
      }
      this.lastCount = signals.length;

      this.el('signals-count').textContent = String(signals.length);
      this.el('signals-updated').textContent = `Updated ${new Date().toLocaleTimeString()}`;

      this.renderFeed(signals);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.el('signals-feed').innerHTML =
        `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private renderFeed(signals: TvSignal[]): void {
    const feed = this.el('signals-feed');

    if (!signals.length) {
      feed.innerHTML = `
        <div class="empty-state">
          <div style="font-size:28px;margin-bottom:10px;">📡</div>
          <div style="font-size:13px;">No TradingView alerts yet.</div>
          <div style="font-size:11px;margin-top:8px;line-height:1.6;">Configure your TradingView webhook to:<br>
            <code style="color:var(--accent);background:var(--bg3);padding:2px 6px;border-radius:2px;">
              POST ${localStorage.getItem('meridian_worker_url') ?? 'https://your-worker.workers.dev'}/api/tv-alert
            </code>
          </div>
        </div>`;
      return;
    }

    feed.innerHTML = signals.map(s => {
      const col = signalColor(s.signal);
      const tf  = s.chart_tf ? `<span style="background:var(--bg3);border:1px solid var(--border);color:var(--text3);padding:1px 5px;border-radius:2px;font-size:9px;margin-left:4px;">${esc(s.chart_tf)}</span>` : '';
      const px  = s.price    ? `<span style="color:var(--text2);font-size:12px;">@ $${esc(s.price)}</span>` : '';
      const exch = s.exchange ? `<span style="color:var(--text3);font-size:10px;">${esc(s.exchange)}</span>` : '';

      return `
        <div style="display:flex;gap:12px;align-items:flex-start;padding:12px 16px;background:var(--panel);border:1px solid var(--border);border-left:3px solid ${col};border-radius:3px;margin-bottom:6px;">
          <div style="flex-shrink:0;text-align:center;min-width:50px;">
            <div style="font-family:var(--mono);font-size:14px;font-weight:700;color:var(--accent);">${esc(s.ticker)}</div>
            ${exch}
          </div>
          <div style="flex:1;min-width:0;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
              <span style="color:${col};font-family:var(--mono);font-size:12px;font-weight:700;text-transform:uppercase;">${esc(s.signal)}</span>
              ${tf}
              ${px}
            </div>
            <div style="color:var(--text3);font-size:10px;font-family:var(--mono);">${timeAgo(s.receivedAt)}</div>
          </div>
          <div style="flex-shrink:0;">
            <button onclick="(window.loadChartTicker||function(){})(${JSON.stringify(s.ticker)})"
              style="background:rgba(0,212,255,0.08);border:1px solid rgba(0,212,255,0.2);color:var(--accent);font-family:var(--mono);font-size:10px;padding:3px 8px;cursor:pointer;border-radius:2px;">📈</button>
          </div>
        </div>`;
    }).join('');
  }
}
